import { CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NotificationContainer } from "react-notifications";
import "react-notifications/lib/notifications.css";
import { shallowEqual, useDispatch, useSelector, batch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { MyService, ProjectService, PublicService, UserService } from "./api";
import { useApi, useEffectOnce } from "./common/hooks";
import { environmentAllowed, environmentSwitch, loadTranslation } from "./common/providers";
import { AppRoutes, NavbarContainer, NavUtils } from "./navigation";
import KeycloakService from "./services/KeycloakService";
import { ErrorBoundary, FocusProvider, ProductTours } from "./common/components";
import {
	setHasEntered,
	setCompanies,
	setSegmentationFlags,
	setToken,
	setCompany,
	setCompaniesToProject,
} from "./common/slice/contextSlice";
import permissions from "./common/providers/permissions/permissions";
import { isNonEmptyArray } from "./common/utils";

const Loader = () => (
	<div style={{ margin: "auto" }}>
		<CircularProgress color="primary" />
	</div>
);

const hasAdminClaim = () => KeycloakService.hasRole([permissions.REALM.ADMIN.claim]);

const Refresh = ({ children }) => {
	const isLogged = KeycloakService.isLoggedIn();
	const user = useSelector(({ context }) => context.user);
	const roles = useSelector(({ context }) => context.roles);
	const loginCompanies = useSelector(({ context }) => context.companies);
	const company = useSelector(({ context }) => context.company);
	const companiesRedux = useSelector(({ context }) => context.companies);
	const companiesToProject = useSelector(({ context }) => context.companiesToProject);
	const location = useLocation();
	const [isLogging, setIsLogging] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { call: getAllUserCompanies } = useApi(UserService.getAllUserCompanies, 1000);
	const { call: getAccessibleCompaniesForProject } = useApi(ProjectService.getAccessibleCompaniesForProject, 1000);
	const { call: logWithCompany } = useApi(UserService.loginWithCompany);
	const { call: getUserDetails } = useApi(MyService.getDetails);
	const { call: getProjectDetails } = useApi(MyService.getProjectDetails, 1000);
	const { isLoading: isRefreshing } = useApi(PublicService.refreshToken);
	const isInProject = useMemo(() => location.pathname.includes("/projects"), [location]);
	const indexProject = useMemo(() => location.pathname.split("/").findIndex((x) => x === "projects") + 1, [location]);
	const projectId = useMemo(() => parseInt(location.pathname.split("/")[indexProject], 10), [location, indexProject]);

	const mainSetCompanies = useCallback(
		(companies) => {
			dispatch(setCompanies(companies?.data || []));
		},
		[dispatch]
	);
	const veoliaSetCompanies = useCallback(
		(companies) => {
			const t = companies.data;
			const userCompanies =
				(isNonEmptyArray(t) &&
					t.map(({ id, companyName, shortName, parentId, parentName, role }) => ({
						id,
						companyName,
						shortName,
						parentId,
						parentName,
						role,
					}))) ||
				[];
			dispatch(setCompanies(userCompanies));
		},
		[dispatch]
	);
	useEffectOnce(
		() =>
			getAllUserCompanies()
				.then((companies) => {
					environmentSwitch({
						main: mainSetCompanies(companies),
						veolia: veoliaSetCompanies(companies),
					});
				})
				.catch(() => {
					setTimeout(() => {
						KeycloakService.doLogout().then(() => {
							KeycloakService.clearToken();
						});
					}, 3000);
				}),
		[getAllUserCompanies, loginCompanies, dispatch],
		() => !loginCompanies && !hasAdminClaim()
	);
	useEffect(
		() => {
			if (
				company?.id &&
				isInProject &&
				!Number.isNaN(projectId) &&
				companiesToProject?.projectId !== projectId &&
				!isLogging &&
				environmentAllowed(["veolia"])
			) {
				getUserDetails()
					.then((userDetailsData) => {
						setIsLogging(true);
						getAccessibleCompaniesForProject({ projectId }).then((data) => {
							if (isNonEmptyArray(data?.values)) {
								dispatch(setCompaniesToProject({ projectId, companies: data?.values }));
								if (!data.values.some((id) => id === userDetailsData.companyId)) {
									logWithCompany({ companyId: data.values[0] })
										.then(() => {
											batch(() => {
												dispatch(
													setCompany(loginCompanies.find((cp) => cp.id === data.values[0]))
												);
												dispatch(setSegmentationFlags());
												dispatch(setToken(KeycloakService.getToken()));
											});
										})
										.catch(console.error)
										.finally(() => setIsLogging(false));
								}
							} else {
								window.location.href = "/";
							}
						});
					})
					.catch(console.error);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[location, company]
	);
	useEffect(
		() => {
			if (isInProject && !Number.isNaN(projectId) && environmentAllowed(["prod", "dev", "qa"])) {
				getProjectDetails({ projectId }).catch(() => {
					window.location.href = "/";
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[location]
	);

	useEffectOnce(
		/* If user has ADMIN Claim, then he's automatically redirected to DSI */
		() => {
			if (hasAdminClaim()) {
				navigate(NavUtils.goToDSI());
				dispatch(setHasEntered());
			}
		},
		[],
		() => hasAdminClaim()
	);
	useEffect(() => {
		if (user?.email && user?.id && posthog.get_distinct_id() !== user.email) {
			posthog.identify(user.email);
			posthog.people.set({
				userId: user.id,
				email: user.email,
				name: user.displayName,
				companies: companiesRedux?.map((c) => c.id) || [],
				aitendersUser: user.email.includes("@aitenders.com"),
			});
		}
	}, [user, roles, companiesRedux]);
	return !isLogged && isRefreshing ? <Loader /> : children;
};

const I18n = ({ children }) => {
	const [render, setRender] = useState(false);
	const { value } = useSelector(({ context }) => context.language);
	const firstTimeLogin = useSelector(({ context }) => context.firstTimeLogin);
	const mountedRef = useRef(false);
	const locale = KeycloakService.userLanguage();
	const language = firstTimeLogin ? locale : value;
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);
	useEffect(() => {
		loadTranslation(language)
			.then(() => (mountedRef.current ? setRender(true) : null))
			.catch((err) => console.error(err));
		return () => (mountedRef.current ? setRender(false) : null);
	}, [language]);
	return render ? children : <Loader />;
};

export default function Root() {
	const hasEntered = useSelector(({ context }) => context.hasEntered, shallowEqual);
	return (
		<FocusProvider>
			<Refresh>
				<I18n>
					<ErrorBoundary>
						{hasEntered && <NavbarContainer />}
						<AppRoutes />
						<NotificationContainer />
						<ProductTours />
					</ErrorBoundary>
				</I18n>
			</Refresh>
		</FocusProvider>
	);
}
